import React from 'react'

interface KeyboardShortcutBadgeProps {
  shortcut: string
  className?: string
}

/**
 * Detect if user is on macOS
 */
const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(navigator.userAgent)

/**
 * Format shortcut for display
 * Supports formats like "n", "Enter", "Mod+Enter" (Mod = Cmd on Mac, Ctrl on others)
 */
const formatShortcut = (shortcut: string): string => {
  if (shortcut.toLowerCase().startsWith('mod+')) {
    const key = shortcut.substring(4)
    const modKey = isMac ? '⌘' : 'Ctrl'
    const displayKey = key === 'Enter' ? '↵' : key
    return `${modKey}${displayKey}`
  }
  return shortcut.toUpperCase()
}

/**
 * Get readable description for accessibility
 */
const getShortcutLabel = (shortcut: string): string => {
  if (shortcut.toLowerCase().startsWith('mod+')) {
    const key = shortcut.substring(4)
    const modKey = isMac ? 'Command' : 'Control'
    return `${modKey}+${key}`
  }
  return shortcut.toUpperCase()
}

/**
 * A subtle badge showing a keyboard shortcut
 */
export const KeyboardShortcutBadge: React.FC<KeyboardShortcutBadgeProps> = ({ 
  shortcut, 
  className = '' 
}) => {
  const displayText = formatShortcut(shortcut)
  const ariaLabel = getShortcutLabel(shortcut)
  
  // Adjust width based on content length
  const widthClass = displayText.length > 1 ? 'px-1.5 min-w-[20px]' : 'h-5 w-5'
  
  return (
    <span 
      className={`ml-auto inline-flex items-center justify-center ${widthClass} rounded text-[10px] font-medium bg-black/10 text-foreground/60 ${className}`}
      aria-label={`Keyboard shortcut: ${ariaLabel}`}
      title={`Press ${ariaLabel} to activate`}
    >
      {displayText}
    </span>
  )
}